import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// @mui
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
// components
import { Page, Iconify, Scrollbar, HeaderBreadcrumbs } from '@/components';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
} from '@/components/table';
import DialogSimple from '@/components/dialog/DialogSimple';
//
import { ClientTableRow } from '../components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { ToolbarWithSearchAndStatus } from '@/components/toolbar';
// API
import {
  deleteUser,
  getClients,
  updateUser,
} from '@/features/users/shared/api';
import { getSettingsData } from '@/features/app/api';
// hooks +  utils
import useTabs from '@/hooks/useTabs';
import useSettings from '@/hooks/useSettings';
import useTable, { getComparator, emptyRows } from '@/hooks/useTable';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
//
import { CLIENTS_TABLE_HEAD, applySortFilterClients } from '../utils';
import { PATH_USER } from '@/features/app';
import { clientManagementPath, ROOTS_CLIENT_MANAGEMENT } from '../routes/paths';
// types
import { User } from '@/features/users/shared/types';

type ClientStatusOption = {
  id: number;
  label: string;
  value: string;
};

// ----------------------------------------------------------------------

export default function ClientList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    setSelected,
    //
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const navigate = useNavigate();
  const allow = useGetAccess('clients');
  const { themeStretch } = useSettings();
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  // =================
  //      QUERIES
  // =================

  // SETTINGS
  const { data: userStatusOptions, isLoading: isSatusOptionsLoading } =
    useQuery({
      queryKey: ['settings'],
      queryFn: getSettingsData,
      onError: (error) => {
        errorMsg(error, `Something went wrong while fetching settings data `);
      },
      select: (data): ClientStatusOption[] =>
        data.userStatuses
          .filter((status) => status.display !== 'Removed')
          .map((status) => ({
            id: status.id,
            label: status.display,
            value: status.display.toLowerCase(),
          })),
    });

  const {
    currentTab: filterStatus,
    onChangeTab: onChangeFilterStatus,
    setCurrentTab,
  } = useTabs('active');

  useEffect(() => {
    if (!userStatusOptions?.length) {
      return;
    }

    if (userStatusOptions.some((status) => status.value === filterStatus)) {
      return;
    }

    const fallbackStatus =
      userStatusOptions.find((status) => status.value === 'active')?.value ||
      userStatusOptions[0].value;
    setCurrentTab(fallbackStatus);
  }, [filterStatus, setCurrentTab, userStatusOptions]);

  const filterStatusOption = userStatusOptions?.find(
    (status) => status.value === filterStatus
  );

  const currentStatusId = filterStatusOption?.id;

  const clientsQueryKey = ['clients', 'list', currentStatusId ?? 'all'];

  const prefetchedStatusIds = useRef<Set<number>>(new Set());

  const { data: clients, isLoading: isClientsDataLoading } = useQuery({
    queryKey: clientsQueryKey,
    enabled: typeof currentStatusId === 'number',
    queryFn: () => getClients(currentStatusId),
    keepPreviousData: true,
    onError: (error) =>
      errorMsg(error, 'There was an error while fetching clients data'),
    onSuccess: () => {
      if (!filterStatusOption || filterStatusOption.value !== 'active') {
        return;
      }

      userStatusOptions
        ?.filter(
          (status) =>
            status.id !== currentStatusId &&
            !prefetchedStatusIds.current.has(status.id)
        )
        .forEach((status) => {
          prefetchedStatusIds.current.add(status.id);
          queryClient.prefetchQuery({
            queryKey: ['clients', 'list', status.id],
            queryFn: () => getClients(status.id),
          });
        });
    },
  });

  // =======================
  //    FILTER/EDIT/DELETE
  // =======================
  const [filterName, setFilterName] = useState('');
  const [bulkStatusId, setBulkStatusId] = useState('');
  const [isBulkStatusDialogOpen, setIsBulkStatusDialogOpen] = useState(false);

  const handleStatusTabChange = (event: SyntheticEvent, value: string) => {
    onChangeFilterStatus(event, value);
    setSelected([]);
    setBulkStatusId('');
    setPage(0);
  };

  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setSelected([]);
    setPage(0);
  };

  const deleteClientMutation = useMutation({
    mutationFn: (id: number) => deleteUser(id),
    onSuccess: (_apiResponse, variables) => {
      if (typeof currentStatusId === 'number') {
        queryClient.setQueryData(
          ['clients', 'list', currentStatusId],
          (old: User[] = []) => old.filter((b) => b.id !== variables)
        );
      }
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setPage(0);
      successMsg(`Client succesfully deleted`);
    },
    onError: (error) => {
      errorMsg(error, `There was an error while deleting client`);
    },
  });

  const bulkUpdateStatusMutation = useMutation({
    mutationFn: ({
      clientIds,
      statusId,
    }: {
      clientIds: number[];
      statusId: number;
    }) =>
      Promise.all(
        clientIds.map((clientId) => updateUser(clientId, { status: statusId }))
      ),
    onSuccess: (_apiResponse, variables) => {
      if (typeof currentStatusId === 'number') {
        queryClient.setQueryData(
          ['clients', 'list', currentStatusId],
          (old: User[] = []) =>
            old.filter((client) => !variables.clientIds.includes(client.id))
        );
      }

      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setSelected([]);
      setBulkStatusId('');
      setIsBulkStatusDialogOpen(false);
      setPage(0);

      const targetStatus = userStatusOptions?.find(
        (status) => status.id === variables.statusId
      );
      const clientCount = variables.clientIds.length;
      successMsg(
        `${clientCount} ${clientCount === 1 ? 'client' : 'clients'} moved to ${
          targetStatus?.label ?? 'selected status'
        }`
      );
    },
    onError: (error) => {
      errorMsg(error, `There was an error while updating client statuses`);
    },
  });

  const handleDeleteRow = async (id: number) => {
    deleteClientMutation.mutate(id);
  };

  const handleEditRow = (id: number) => {
    navigate(
      clientManagementPath('edit', { userId: String(id), tabSid: 'details' })
    );
  };

  const selectedClientIds = selected
    .map((clientId) => Number(clientId))
    .filter((clientId) => Number.isFinite(clientId));

  const bulkStatusOptions = (userStatusOptions || []).filter(
    (status) => status.id !== currentStatusId
  );

  const selectedBulkStatusOption = bulkStatusOptions.find(
    (status) => String(status.id) === bulkStatusId
  );

  const handleOpenBulkStatusDialog = () => {
    if (!selectedClientIds.length) {
      return;
    }

    setBulkStatusId('');
    setIsBulkStatusDialogOpen(true);
  };

  const handleCloseBulkStatusDialog = () => {
    setBulkStatusId('');
    setIsBulkStatusDialogOpen(false);
  };

  const handleBulkStatusUpdate = () => {
    const statusId = Number(bulkStatusId);

    if (!Number.isFinite(statusId) || !selectedClientIds.length) {
      return;
    }

    bulkUpdateStatusMutation.mutate({
      clientIds: selectedClientIds,
      statusId,
    });
  };

  const dataFiltered = applySortFilterClients({
    tableData: clients || [],
    comparator: getComparator(order, orderBy),
    filterName,
    filterStatus,
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterStatus) ||
    !dataFiltered.length;

  const showClientsSkeleton = isClientsDataLoading || !filterStatusOption;

  // ----------------------------------------------------------------------

  return (
    <>
      <DialogSimple
        dialogTitle="Change client status"
        open={isBulkStatusDialogOpen}
        onClickButtonClose={handleCloseBulkStatusDialog}
        onClickButtonLoading={handleBulkStatusUpdate}
        loadingButton
        isSubmitting={bulkUpdateStatusMutation.isLoading}
        disableLoadingButton={
          bulkUpdateStatusMutation.isLoading || !bulkStatusId
        }
        loadingButtonText="Change Status"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
      >
        <Box sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set the status for {selectedClientIds.length}{' '}
            {selectedClientIds.length === 1
              ? 'selected client'
              : 'selected clients'}
            .
          </Typography>
          <TextField
            select
            fullWidth
            label="Status"
            value={bulkStatusId}
            onChange={(event) => setBulkStatusId(event.target.value)}
          >
            {bulkStatusOptions.map((status) => (
              <MenuItem key={status.id} value={String(status.id)}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
          {selectedBulkStatusOption && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              These clients will be moved to {selectedBulkStatusOption.label}.
            </Typography>
          )}
        </Box>
      </DialogSimple>

      <Page title="Clients: List">
        <Container maxWidth={themeStretch ? false : 'lg'}>
          <HeaderBreadcrumbs
            heading="Client List"
            links={[
              { name: 'Dashboard', href: PATH_USER.root },
              { name: 'Client', href: ROOTS_CLIENT_MANAGEMENT },

              { name: 'List' },
            ]}
            action={
              allow.canCreate && (
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={clientManagementPath('new')}
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                >
                  Add Client
                </Button>
              )
            }
          />

          <Card>
            {showClientsSkeleton ? (
              <SkeletonLoadingTable />
            ) : (
              <>
                {isSatusOptionsLoading ? (
                  <Skeleton variant="rounded" height={56} />
                ) : (
                  <Tabs
                    allowScrollButtonsMobile
                    variant="scrollable"
                    scrollButtons="auto"
                    value={filterStatusOption?.value || filterStatus}
                    onChange={handleStatusTabChange}
                    sx={{ px: 2, bgcolor: 'background.neutral' }}
                  >
                    {(userStatusOptions || []).map((status) => (
                      <Tab
                        disableRipple
                        key={status.id}
                        label={status.label}
                        value={status.value}
                      />
                    ))}
                  </Tabs>
                )}

                <Divider />

                <ToolbarWithSearchAndStatus
                  filterText={filterName}
                  onFilterText={handleFilterName}
                  labels={{ search: 'Search in clients...' }}
                />

                <Scrollbar>
                  <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
                    {selected.length > 0 && allow.canUpdate && (
                      <TableSelectedActions
                        dense={dense}
                        numSelected={selected.length}
                        rowCount={dataFiltered.length}
                        onSelectAllRows={(checked) =>
                          onSelectAllRows(
                            checked,
                            dataFiltered.map((row) => String(row.id))
                          )
                        }
                        actions={
                          <Tooltip title="Change status">
                            <span>
                              <IconButton
                                color="primary"
                                disabled={!bulkStatusOptions.length}
                                aria-label="Change status for selected clients"
                                onClick={handleOpenBulkStatusDialog}
                              >
                                <Iconify icon="eva:checkmark-square-2-outline" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      />
                    )}

                    <Table size={dense ? 'small' : 'medium'}>
                      <TableHeadCustom
                        order={order}
                        orderBy={orderBy}
                        headLabel={CLIENTS_TABLE_HEAD}
                        rowCount={dataFiltered.length}
                        numSelected={selected.length}
                        onSort={onSort}
                        onSelectAllRows={
                          allow.canUpdate
                            ? (checked) =>
                                onSelectAllRows(
                                  checked,
                                  dataFiltered.map((row) => String(row.id))
                                )
                            : undefined
                        }
                      />
                      <TableBody>
                        {dataFiltered
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((client) => (
                            <ClientTableRow
                              key={client.id}
                              row={client}
                              selected={selected.includes(String(client.id))}
                              selectionEnabled={allow.canUpdate}
                              onSelectRow={() => onSelectRow(String(client.id))}
                              onDeleteRow={() =>
                                handleDeleteRow(Number(client.id))
                              }
                              onEditRow={() => handleEditRow(client.id)}
                              canDelete={allow.canDelete}
                              canUpdate={allow.canUpdate}
                            />
                          ))}

                        <TableEmptyRows
                          height={denseHeight}
                          emptyRows={emptyRows(
                            page,
                            rowsPerPage,
                            dataFiltered.length
                          )}
                        />

                        <TableNoData
                          isNotFound={isNotFound}
                          title="No clients found"
                        />
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                <Box sx={{ position: 'relative' }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={dataFiltered.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                  />

                  <FormControlLabel
                    control={
                      <Switch checked={dense} onChange={onChangeDense} />
                    }
                    label="Dense"
                    sx={{
                      px: 3,
                      py: 1.5,
                      top: 0,
                      position: { md: 'absolute' },
                    }}
                  />
                </Box>
              </>
            )}
          </Card>
        </Container>
      </Page>
    </>
  );
}
